// ============================================
// document-person-mention-popover.tsx — R25 Bug B
// ============================================
//
// Left-clicking an `@` person mention in document mode used to fire a
// "Mention sent" toast (`document.mention.notified`). That is a comment
// behaviour and makes no sense in a document: nothing was sent.
//
// This popover replaces it. It is anchored to the clicked mention chip
// and lists the tasks ASSIGNED to that person in the current project,
// each with its status badge. Clicking a row opens the existing
// right-hand task detail panel (via the parent's `onOpenTask`) and
// closes the popover.
//
// Data: `GET /api/projects/:projectId/tasks?assignee=<userId>&limit=100&offset=0`
// — fetched LAZILY, only while the popover is open (React Query
// `enabled`), so merely rendering a document never triggers a request
// per mention.
//
// It reuses the shared Radix popover primitive
// (`@/components/ui/popover`) and the status maps exported by
// `document-mention-list.tsx` so the badges match the picker exactly.

import { useEffect, useMemo, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useDocumentI18n } from './document-i18n';
import {
    TASK_STATUS_BADGE,
    TASK_STATUS_LABEL,
} from './document-mention-list';
import { usePublicProjectReader, publicQueryKey, projectReaderGet } from '@/lib/public-project-reader';
import { fetchAssigneeTasks, buildAssigneeTasksEndpoint, type MentionTaskPage } from './document-mention-search';

/** Backend max for `limit` — one page is plenty for a person's tasks. */
export const PERSON_MENTION_TASKS_LIMIT = 100;

/** Who the popover is about. */
export interface DocumentPersonMentionTarget {
    /** User id encoded in the Mention node. */
    id: string;
    /** Display name written into the Mention node label. */
    label: string;
    avatarUrl?: string | null;
    email?: string | null;
}

export interface DocumentPersonMentionPopoverProps {
    projectId: string;
    /** The mentioned person, or null when the popover is closed. */
    target: DocumentPersonMentionTarget | null;
    /**
     * The clicked mention chip. Radix's `PopoverAnchor` accepts a
     * virtual ref, so we can anchor to a DOM element that lives inside
     * the ProseMirror node view (outside this React subtree) without
     * rendering an extra wrapper around the chip.
     */
    anchorRef: RefObject<HTMLElement | null>;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Opens the right-hand task detail panel. */
    onOpenTask?: (taskId: string) => void;
}

function initialsFor(label: string, email?: string | null): string {
    const source = label || email || '?';
    return source
        .split(/\s+|@/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? '')
        .join('');
}

export function DocumentPersonMentionPopover({
    projectId,
    target,
    anchorRef,
    open,
    onOpenChange,
    onOpenTask,
}: DocumentPersonMentionPopoverProps) {
    const { t } = useDocumentI18n();
    const reader=usePublicProjectReader();
    // Radix's virtualRef type comes from the transitive `@radix-ui/rect`
    // package (not directly resolvable under pnpm's strict linking), so
    // we borrow the exact prop type from the primitive itself.
    //
    // R26 fix (mention popup landed in the top-left corner of the screen
    // inside tables): the anchor used to be the DOM node captured during
    // the click. ProseMirror REPLACES a table cell's DOM when a remote
    // Yjs update (or a table re-render) touches it, and a detached node
    // measures as `{0,0,0,0}` — floating-ui then parks the popup at the
    // viewport origin. We now hand Radix a LAZY anchor: floating-ui calls
    // `getBoundingClientRect()` on it every time it measures, so we
    // re-resolve the chip by its stable attributes, then fall back to the
    // caret and finally to the editor box. A zero-sized rect is never
    // returned, so the popup can no longer jump to the top-left.
    const personId = target?.id ?? null;
    const virtualAnchor = useMemo(() => {
        const measure = (): DOMRect => {
            const fromElement = (el: Element | null): DOMRect | null => {
                if (!el || !el.isConnected) return null;
                const rect = el.getBoundingClientRect();
                if (rect.width === 0 && rect.height === 0) return null;
                return rect;
            };
            const captured = fromElement(anchorRef.current);
            if (captured) return captured;
            // The node we captured may have been replaced by a re-render:
            // find the chip that stands for the same person now.
            if (personId) {
                const selector = `[data-mention-kind="person"][data-person-id="${CSS.escape(
                    personId
                )}"]`;
                const candidates = document.querySelectorAll(selector);
                for (const candidate of Array.from(candidates)) {
                    const rect = fromElement(candidate);
                    if (rect) return rect;
                }
            }
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const rect = fromElement(selection.getRangeAt(0).startContainer.parentElement)
                    ?? selection.getRangeAt(0).getBoundingClientRect();
                if (rect && (rect.width > 0 || rect.height > 0)) return rect;
            }
            const editor = document.querySelector(
                '[data-testid="document-editor-content"]'
            );
            const editorRect = fromElement(editor);
            if (editorRect) return editorRect;
            // Absolute last resort: keep it on screen rather than at 0,0.
            const { innerWidth, innerHeight } = window;
            return new DOMRect(innerWidth / 2, innerHeight / 2, 0, 0);
        };
        return {
            current: { getBoundingClientRect: measure },
        } as unknown as React.ComponentProps<typeof PopoverAnchor>['virtualRef'];
    }, [anchorRef, personId]);

    const enabled = open && !!target && !!projectId;
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: publicQueryKey(reader, [
            'document-person-mention-tasks',
            projectId,
            target?.id ?? null,
        ]),
        queryFn: () => reader ? projectReaderGet<MentionTaskPage>(reader, buildAssigneeTasksEndpoint(projectId,target!.id)) : fetchAssigneeTasks(projectId, target!.id),
        enabled,
        // R26: always show the CURRENT tasks. The popup used to serve a
        // 30 s-stale cache, so editing a task in the right-hand panel and
        // then reopening the mention popup showed the old status/title.
        // Nothing else invalidates this key (task mutations only
        // invalidate the task-list keys), so the popup refreshes on every
        // open instead of relying on cache expiry.
        staleTime: 0,
        refetchOnMount: 'always',
    });

    // Every open re-fetches the assignee's tasks, so a task edited in the
    // side panel is reflected the next time the mention is clicked.
    useEffect(() => {
        if (!enabled) return;
        void refetch();
    }, [enabled, refetch]);

    const tasks = data?.tasks ?? [];

    if (!target) return null;

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverAnchor virtualRef={virtualAnchor} />
            <PopoverContent
                align="start"
                side="bottom"
                sideOffset={6}
                className="w-80 p-0"
                data-testid="document-person-mention-popover"
                // Keep the editor selection intact; Radix focuses the
                // content on open, which is what we want for keyboard
                // dismissal (Escape) without touching the document.
                aria-label={t('document.mention.personTasksTitle')}
            >
                <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                    <Avatar className="h-7 w-7 shrink-0">
                        {target.avatarUrl ? (
                            <AvatarImage
                                src={target.avatarUrl}
                                alt={target.label}
                            />
                        ) : null}
                        <AvatarFallback className="text-[10px] font-semibold">
                            {initialsFor(target.label, target.email)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <p
                            className="truncate text-sm font-medium text-foreground"
                            data-testid="document-person-mention-popover-name"
                        >
                            {target.label}
                        </p>
                        <p className="text-[11px] text-foreground-muted">
                            {t('document.mention.personTasksTitle')}
                        </p>
                    </div>
                    {!isLoading && !isError ? (
                        <span className="shrink-0 text-[11px] text-foreground-muted">
                            {tasks.length}
                        </span>
                    ) : null}
                </div>

                <div className="max-h-72 overflow-auto p-1">
                    {isLoading ? (
                        <div
                            className="flex items-center gap-2 px-2 py-2 text-xs text-foreground-muted"
                            data-testid="document-person-mention-loading"
                        >
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {t('document.mention.loading')}
                        </div>
                    ) : isError ? (
                        <div
                            className="px-2 py-2 text-xs text-amber-400/80"
                            data-testid="document-person-mention-error"
                        >
                            {t('document.mention.personTasksError')}
                        </div>
                    ) : tasks.length === 0 ? (
                        <div
                            className="px-2 py-2 text-xs text-foreground-muted"
                            data-testid="document-person-mention-empty"
                        >
                            {t('document.mention.personTasksEmpty')}
                        </div>
                    ) : (
                        tasks.map((task) => (
                            <button
                                key={task.id}
                                type="button"
                                data-testid="document-person-mention-task"
                                data-task-id={task.id}
                                onClick={() => {
                                    // Open the existing right-hand task
                                    // detail panel, then dismiss.
                                    onOpenTask?.(task.id);
                                    onOpenChange(false);
                                }}
                                className={cn(
                                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors',
                                    'text-foreground-muted hover:bg-white/5 hover:text-foreground'
                                )}
                            >
                                <span className="min-w-0 flex-1 truncate">
                                    {task.title}
                                </span>
                                <span
                                    className={cn(
                                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                                        TASK_STATUS_BADGE[task.status]
                                    )}
                                >
                                    {TASK_STATUS_LABEL[task.status]}
                                </span>
                            </button>
                        ))
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
